/**
 * Zoom by Cmd/Ctrl + mouse wheel and by touchpad pinch — pure maths, no React / DOM writes.
 *
 * Every zoom in the editor is a percentage, so the same helpers serve the canvas and the
 * timeline; only the limits and the way the view is re-anchored differ.
 */

/**
 * Exponent per wheel px: the zoom is multiplied by `2 ^ (-px * WHEEL_ZOOM_SPEED)`.
 *
 * Exponential so that zooming in and back out by the same amount lands on the same zoom,
 * and a step feels the same at 20 % as at 300 %.
 */
const WHEEL_ZOOM_SPEED = 0.01

/**
 * Cap on one event's delta.
 *
 * A pinch arrives as a stream of tiny deltas (a few px each), a mouse wheel notch as one
 * large one (~100 px, more with acceleration). Without the cap a single notch would zoom
 * 2× — with it a notch is ~23 %, while a pinch stays as fine-grained as the touchpad reports.
 */
const MAX_WHEEL_DELTA_PX = 30

/** `WheelEvent.deltaMode` — Firefox reports a mouse wheel in lines, not pixels. */
const DOM_DELTA_LINE = 1
const DOM_DELTA_PAGE = 2
const LINE_HEIGHT_PX = 16
const PAGE_HEIGHT_PX = 800

/** The vertical wheel delta in px, whatever unit the browser reported it in. */
export const wheelDeltaPx = (event: Pick<WheelEvent, "deltaY" | "deltaMode">): number => {
    if (event.deltaMode === DOM_DELTA_LINE) return event.deltaY * LINE_HEIGHT_PX
    if (event.deltaMode === DOM_DELTA_PAGE) return event.deltaY * PAGE_HEIGHT_PX
    return event.deltaY
}

/** Wheel down / pinch in zooms out, wheel up / pinch out zooms in. */
export const zoomByWheel = (zoom: number, deltaPx: number): number => {
    const capped = Math.max(-MAX_WHEEL_DELTA_PX, Math.min(MAX_WHEEL_DELTA_PX, deltaPx))
    return zoom * Math.pow(2, -capped * WHEEL_ZOOM_SPEED)
}

/** Two decimals are plenty for a percentage and keep the persisted value readable. */
export const roundZoom = (zoom: number): number => Math.round(zoom * 100) / 100

/**
 * Where the content has to scroll so that the point under the pointer stays under it.
 *
 * @param contentOffset the anchored point's distance from the content's origin, at `oldZoom`
 * @param viewportOffset the pointer's distance from the scroll container's edge
 * @returns the new scroll offset (the browser clamps it to the scrollable range)
 */
export const anchoredScroll = (
    contentOffset: number,
    oldZoom: number,
    newZoom: number,
    viewportOffset: number
): number => (contentOffset * newZoom) / oldZoom - viewportOffset
